import { useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";

// ── CHECKLIST TEMPLATE ───────────────────────────────────────────────
// scope: "site" = one per site, "wtg" = per WTG location
// readiness: DevCo's self-assessed readiness (Ready/Partial/Not Ready) — only for non-accepted WTGs
var CATS = [
  { id: "PS", name: "Physical Site", items: [
    { id: "PS01", metric: "Plot demarcation & coordinates", layer: "non-neg", scope: "wtg", docs: [
      { id: "PS01a", name: "Signed Joint Measurement Certificate (DevCo + Project)", xlMatch: "Joint Measurement Certificate" },
      { id: "PS01b", name: "Final coordinate pack (.KMZ/.DWG/.DXF)", xlMatch: "coordinate pack" },
    ]},
    { id: "PS02", metric: "Usable working area & temporary rights", layer: "non-neg", scope: "wtg", docs: [
      { id: "PS02a", name: "Overlay Map (crane hardstand onto acquired land)", xlMatch: "Overlay Map" },
      { id: "PS02b", name: "Signed Consent Letters for temporary use", xlMatch: "Consent Letters" },
      { id: "PS02c", name: "Obstruction Log with photos & mitigation timeline", xlMatch: "Obstruction Log" },
    ]},
    { id: "PS03", metric: "Storage yard readiness", layer: "ntp-b", scope: "site", docs: [
      { id: "PS03a", name: "Approved Yard Layout Drawing", xlMatch: "Yard Layout" },
      { id: "PS03b", name: "Registered Lease Deed", xlMatch: "Registered Lease Deed" },
      { id: "PS03c", name: "Route/Access Survey Note for yard", xlMatch: "Survey Note for yard" },
    ]},
    { id: "PS04", metric: "Handover schedule adherence", layer: "ntp-a", scope: "wtg", docs: [
      { id: "PS04a", name: "Signed Approval for deviation from standard plan/specs", xlMatch: "deviation from standard" },
    ]},
    { id: "PS05", metric: "Temporary land for tower assembly", layer: "ntp-b", scope: "wtg", docs: [
      { id: "PS05a", name: "Signed consent/lease for temporary assembly area", xlMatch: null },
      { id: "PS05b", name: "Area clearance & levelling confirmation note", xlMatch: null },
    ]},
    { id: "PS06", metric: "Land scraping & pad preparation", layer: "ntp-a", scope: "wtg", docs: [
      { id: "PS06a", name: "Land scraping completion report with photos", xlMatch: null },
      { id: "PS06b", name: "Boundary and center stone marking confirmation", xlMatch: null },
    ]},
    { id: "PS07", metric: "Crane pad & boom area readiness", layer: "ntp-c", scope: "wtg", docs: [
      { id: "PS07a", name: "Crane pad development completion report", xlMatch: null },
      { id: "PS07b", name: "Boom area clearance & development confirmation", xlMatch: null },
    ]},
  ]},
  { id: "RA", name: "RoW & Access", items: [
    { id: "RA01", metric: "Access road from zero point to WTG", layer: "non-neg", scope: "wtg", docs: [
      { id: "RA01a", name: "Route survey / drive-through report", xlMatch: "Route survey" },
      { id: "RA01b", name: "Temporary Permissions/NOC for main access routes", xlMatch: "Permissions/NOC for main access" },
    ]},
    { id: "RA02", metric: "Physical survey for RoW corridor", layer: "ntp-a", scope: "wtg", docs: [
      { id: "RA02a", name: "Chainage-wise map (survey numbers, RoW overlay)", xlMatch: "Chainage-wise map" },
    ]},
    { id: "RA03", metric: "Patwari verification of Route Survey Map", layer: "ntp-a", scope: "wtg", docs: [
      { id: "RA03a", name: "Village-wise Patwari verification note", xlMatch: "Patwari verification" },
    ]},
    { id: "RA04", metric: "Farmer KYC + compensation affidavit", layer: "non-neg", scope: "wtg", docs: [
      { id: "RA04a", name: "KYC pack (Aadhaar, photo, bank passbook)", xlMatch: "KYC pack" },
      { id: "RA04b", name: "Affidavit of compensation receipt", xlMatch: "Affidavit of compensation" },
    ]},
    { id: "RA05", metric: "Location Pathway", layer: "ntp-a", scope: "wtg", docs: [
      { id: "RA05a", name: "Pathway RoW map", xlMatch: "Pathway RoW map" },
      { id: "RA05b", name: "Registered Easement Deed / Lease", xlMatch: "Easement Deed" },
    ]},
    { id: "RA06", metric: "Route deviation control (33kV + pathways)", layer: "ntp-a", scope: "wtg", docs: [
      { id: "RA06a", name: "Deviation Clearance note", xlMatch: "Deviation Clear" },
    ]},
    { id: "RA07", metric: "Crane pathway & bellmouth readiness", layer: "ntp-c", scope: "wtg", docs: [
      { id: "RA07a", name: "Crane pathway compliance report (road width, bellmouth, CBR)", xlMatch: null },
      { id: "RA07b", name: "Easement rights/NOC for crane pathway widening", xlMatch: null },
    ]},
  ]},
  { id: "LT", name: "Legal Title", items: [
    { id: "LT01", metric: "Title clearance / DDR", layer: "non-neg", scope: "wtg", docs: [
      { id: "LT01a", name: "TSR + DDR (30-year title search)", xlMatch: "TSR" },
      { id: "LT01b", name: "Non-Encumbrance Certificate", xlMatch: "Non-Encumbrance" },
      { id: "LT01c", name: "Bank/society NOC (if applicable)", xlMatch: "Bank/society NOC" },
    ]},
    { id: "LT02", metric: "Farmer KYC + compensation affidavit (all parties)", layer: "non-neg", scope: "wtg", docs: [
      { id: "LT02a", name: "KYC pack (all parties)", xlMatch: "KYC pack for farmer" },
    ]},
    { id: "LT03", metric: "Conveyance execution", layer: "non-neg", scope: "wtg", docs: [
      { id: "LT03a", name: "Sale Deed or Lease Deed + stamp duty", xlMatch: "Sale Deed or Lease Deed" },
    ]},
    { id: "LT04", metric: "RTC/Mutation records verified", layer: "ntp-a", scope: "wtg", docs: [
      { id: "LT04a", name: "Verification Checklist (RTC/Mutation/7-12/Jamabandi)", xlMatch: "RTC/Mutation" },
    ]},
    { id: "LT05", metric: "Physical possession verified vs RTC/Mutation", layer: "non-neg", scope: "wtg", docs: [
      { id: "LT05a", name: "Possession verification note from site head", xlMatch: "Possession verification" },
    ]},
    { id: "LT06", metric: "Land-use conversion / NA / zoning", layer: "ntp-a", scope: "wtg", docs: [
      { id: "LT06a", name: "NA order", xlMatch: "NA order" },
      { id: "LT06b", name: "Town planning / zoning NOC", xlMatch: "Town planning" },
      { id: "LT06c", name: "Deviation approval / Risk acceptance note", xlMatch: "Risk acceptance note" },
    ]},
  ]},
  { id: "PM", name: "Permits & Statutory", items: [
    { id: "PM01", metric: "Permit matrix completeness", layer: "general", scope: "site", docs: [
      { id: "PM01a", name: "Permit tracker (conditions, compliance owners, renewal)", xlMatch: "Permit tracker" },
    ]},
    { id: "PM02", metric: "State Nodal Agency Approval", layer: "non-neg", scope: "wtg", docs: [
      { id: "PM02a", name: "RRECL / GEDA / KREDL approval", xlMatch: "State Nodal Agency" },
    ]},
    { id: "PM03", metric: "Forest permit/NOC", layer: "non-neg", scope: "wtg", docs: [
      { id: "PM03a", name: "Forest clearance document", xlMatch: "Forest permit" },
    ]},
    { id: "PM04", metric: "Mining/Excavation NOC", layer: "non-neg", scope: "wtg", docs: [
      { id: "PM04a", name: "Mining NOC document", xlMatch: "Mining/Excavation" },
    ]},
    { id: "PM05", metric: "Environment permit/NOC", layer: "non-neg", scope: "wtg", docs: [
      { id: "PM05a", name: "Environment clearance document", xlMatch: "Environment permit" },
    ]},
    { id: "PM06", metric: "Local body + Gram Panchayat NOC", layer: "non-neg", scope: "wtg", docs: [
      { id: "PM06a", name: "Local body permission", xlMatch: "Local body permission" },
      { id: "PM06b", name: "Gram Panchayat NOC", xlMatch: "Gram Panchayat" },
    ]},
    { id: "PM07", metric: "Aviation/Defence clearance", layer: "non-neg", scope: "wtg", docs: [
      { id: "PM07a", name: "Aviation/Defence clearance document", xlMatch: "Aviation/Defence" },
    ]},
    { id: "PM08", metric: "Exception approval", layer: "non-neg", scope: "wtg", docs: [
      { id: "PM08a", name: "Exception approval (if any)", xlMatch: "Exception approval" },
    ]},
  ]},
  { id: "CC", name: "Customer / Commercial", items: [
    { id: "CC01", metric: "Client approvals (parcels / TSR-DDR / layouts)", layer: "ntp-b", scope: "wtg", docs: [
      { id: "CC01a", name: "Client approval emails/letters", xlMatch: "Client approval" },
      { id: "CC01b", name: "NTP (if issued)", xlMatch: "NTP" },
    ]},
    { id: "CC02", metric: "Change-control baseline (post RO/DO)", layer: "ntp-c", scope: "wtg", docs: [
      { id: "CC02a", name: "Approved change log and impact note", xlMatch: "change log" },
      { id: "CC02b", name: "Updated drawings", xlMatch: "Updated drawings" },
    ]},
  ]},
  { id: "SC", name: "Stakeholder / Community", items: [
    { id: "SC01", metric: "Stakeholder stability", layer: "general", scope: "site", docs: [
      { id: "SC01a", name: "Stakeholder map (key influencers, stance)", xlMatch: "Stakeholder map" },
    ]},
    { id: "SC02", metric: "Grievance register", layer: "general", scope: "site", docs: [
      { id: "SC02a", name: "Community complaints log with resolution status", xlMatch: "Grievance register" },
    ]},
  ]},
  { id: "FC", name: "Finance / Controls", items: [
    { id: "FC01", metric: "Payment trail & liabilities", layer: "non-neg", scope: "wtg", docs: [
      { id: "FC01a", name: "Consideration register (deed vs paid, RTGS/UTR)", xlMatch: "Consideration register" },
      { id: "FC01b", name: "Escrow statements (if used)", xlMatch: "Escrow" },
      { id: "FC01c", name: "Liability/claims register", xlMatch: "Liability/claims" },
    ]},
  ]},
  { id: "CL", name: "Formal Closure", items: [
    { id: "CL01", metric: "Joint walkover + acceptance note", layer: "general", scope: "wtg", docs: [
      { id: "CL01a", name: "Handover Certificate / HOTO Note + punch list tracker", xlMatch: "Handover Certificate" },
    ]},
  ]},
];

// Flatten
var ALL_ITEMS=[],ALL_DOCS=[],SITE_ITEMS=[],WTG_ITEMS=[],SITE_DOCS=[],WTG_DOCS=[];
CATS.forEach(function(c){c.items.forEach(function(it){
  var e={...it,catId:c.id,catName:c.name};ALL_ITEMS.push(e);
  if(it.scope==="site")SITE_ITEMS.push(e);else WTG_ITEMS.push(e);
  it.docs.forEach(function(d){
    var ed={...d,itemId:it.id,itemMetric:it.metric,layer:it.layer,scope:it.scope,catId:c.id,catName:c.name};
    ALL_DOCS.push(ed);if(it.scope==="site")SITE_DOCS.push(ed);else WTG_DOCS.push(ed);
  });
});});

function blocksWhat(l){if(l==="non-neg"||l==="ntp-a")return"Foundation";if(l==="ntp-b")return"Tower Assembly";if(l==="ntp-c")return"Erection";return"Full HOTO";}
function calcItem(itDocs,docs){var d=0,n=0,t=itDocs.length;itDocs.forEach(function(x){var s=docs[x.id];if(s&&s.na)n++;else if(s&&s.done)d++;});var a=t-n;if(a===0)return{status:"na",done:d,total:t,na:n};if(d>=a)return{status:"complete",done:d,total:t,na:n};if(d>0)return{status:"partial",done:d,total:t,na:n};return{status:"incomplete",done:0,total:t,na:n};}
function layerOk(items,st,layer){return items.filter(function(it){return it.layer===layer;}).every(function(it){var s=st[it.id];return s.status==="complete"||s.status==="na"||s.status==="partial";});}

// ── EXCEL PARSER ─────────────────────────────────────────────────────
// Maps uploaded Excel template to our data model
function parseExcel(arrayBuffer) {
  try {
    var wb = XLSX.read(arrayBuffer, { type: "array" });

    // Parse location details from Handover Checklist sheet
    var clSheet = wb.Sheets[wb.SheetNames[0]];
    var clData = XLSX.utils.sheet_to_json(clSheet, { header: 1, defval: "" });

    var loc = {};
    for (var i = 0; i < Math.min(20, clData.length); i++) {
      var row = clData[i];
      var label = String(row[2] || "").trim();
      var val = String(row[3] || row[4] || "").trim();
      if (label === "WTG Location") loc.wtgLocation = val;
      if (label === "WTG Model") loc.wtgModel = val;
      if (label === "Site") loc.site = val;
      if (label === "Village") loc.village = val;
      if (label === "Taluka") loc.taluka = val;
      if (label === "State") loc.state = val;
      if (label === "Latitude") loc.lat = val;
      if (label === "Longitude") loc.lng = val;
      if (label === "Financial Year") loc.fy = val;
      if (label === "Name of Customer") loc.customer = val;
      if (label === "Workfront for Foundation Date") loc.foundationDate = val;
      if (label === "HOTO Offer Date") loc.offerDate = val;
      if (label === "HOTO Acceptance") loc.acceptance = val;
      if (label === "Final HOTO Date for WTG") loc.finalHotoDate = val;
    }

    // Parse doc statuses — match by xlMatch keyword against column 3 (File/Document)
    // Col 3 = doc text, Col 4 = Mandatory flag (Y/blank), Col 5 = Status (Complete/Incomplete/N/A), Col 6 = File Link
    var docStatuses = {};
    ALL_DOCS.forEach(function(d) { docStatuses[d.id] = { done: false, na: false, fileLink: "" }; });

    // Build a flat list of Excel rows with their doc text and status
    var xlDocs = [];
    for (var r = 17; r < clData.length; r++) {
      var rw = clData[r];
      var docText = String(rw[3] || "").trim();
      var status = String(rw[5] || "").trim().toLowerCase();
      var fileLink = String(rw[6] || "").trim();
      if (docText) xlDocs.push({ text: docText, status: status, fileLink: fileLink, row: r });
    }

    // Match each of our docs to Excel rows
    ALL_DOCS.forEach(function(d) {
      if (!d.xlMatch) return; // New items not in original template — default incomplete
      var match = xlDocs.find(function(x) {
        return x.text.toLowerCase().indexOf(d.xlMatch.toLowerCase()) >= 0;
      });
      if (match) {
        var st = match.status;
        if (st === "complete" || st === "done" || st === "yes" || st === "y" || st === "✓" || st === "completed") {
          docStatuses[d.id] = { done: true, na: false, fileLink: match.fileLink };
        } else if (st === "n/a" || st === "na" || st === "not applicable") {
          docStatuses[d.id] = { done: false, na: true, fileLink: "" };
        }
        // else remains incomplete (false, false)
      }
    });

    // Parse Handover Certificate + Summary sheet
    var certSheet = wb.Sheets[wb.SheetNames[1]];
    var certData = certSheet ? XLSX.utils.sheet_to_json(certSheet, { header: 1, defval: "" }) : [];

    var offer = { date: "", siteHead: "Pending", legalIC: "Pending", techIC: "Pending", acceptance: "Pending", projMgr: "Pending", mechHoD: "Pending", matMove: "Pending" };
    for (var c2 = 0; c2 < certData.length; c2++) {
      var cr = certData[c2];
      var lbl = String(cr[0] || "").trim();
      if (lbl === "HOTO Offer Date") offer.date = String(cr[1] || "");
      if (lbl === "HOTO Acceptance") offer.acceptance = String(cr[1] || "");
      if (lbl === "Land Team Approval" || String(cr[1] || "").trim() === "Site Head") {
        if (String(cr[1] || "").trim() === "Site Head") offer.siteHead = String(cr[2] || "Pending");
      }
      if (String(cr[1] || "").trim() === "Site Legal I/C") offer.legalIC = String(cr[2] || "Pending");
      if (String(cr[1] || "").trim() === "Site Tech I/C") offer.techIC = String(cr[2] || "Pending");
      if (String(cr[1] || "").trim() === "Project Interface Manager") offer.projMgr = String(cr[2] || "Pending");
      if (String(cr[1] || "").trim() === "Mechanical HoD") offer.mechHoD = String(cr[2] || "Pending");
      if (String(cr[1] || "").trim() === "Material & Movement") offer.matMove = String(cr[2] || "Pending");
    }

    // Parse Punchlist Tracker sheet
    var ppSheet = wb.Sheets[wb.SheetNames[2]];
    var ppData = ppSheet ? XLSX.utils.sheet_to_json(ppSheet, { header: 1, defval: "" }) : [];
    var punchPoints = [];
    for (var p = 2; p < ppData.length; p++) {
      var pr = ppData[p];
      if (!pr[1] || String(pr[1]).trim() === "") continue;
      punchPoints.push({
        sno: pr[0] || (p - 1),
        desc: String(pr[1] || ""),
        itemId: "", itemMetric: "", blocks: "", layer: "", scope: "wtg",
        owner: String(pr[2] || ""),
        approver: String(pr[3] || ""),
        expectedDate: String(pr[4] || ""),
        ownerRemarks: String(pr[5] || ""),
        approverRemarks: String(pr[6] || ""),
        closureDate: String(pr[7] || ""),
        status: pr[7] && String(pr[7]).trim() ? "Closed" : (pr[5] && String(pr[5]).trim() ? "In Progress" : "Open"),
      });
    }

    return { loc: loc, docStatuses: docStatuses, offer: offer, punchPoints: punchPoints, parsed: true };
  } catch (e) {
    return { error: String(e), parsed: false };
  }
}

// Build WTG from parsed Excel data
function buildFromExcel(parsed, label, siteId) {
  var docs = {};
  WTG_DOCS.forEach(function(d) {
    var ps = parsed.docStatuses[d.id];
    docs[d.id] = ps || { done: false, na: false, fileLink: "" };
  });
  var items = {};
  WTG_ITEMS.forEach(function(it) { items[it.id] = calcItem(it.docs, docs); });

  // Auto-generate punch points for incomplete docs
  var excelPPs = parsed.punchPoints || [];
  var autoPPs = [];
  var ppIdx = excelPPs.length + 1;
  var dummyOwners = ["Site Head", "Site Legal I/C", "Site Tech I/C", "Circle Head"];
  var dummyApprovers = ["Project Interface Mgr", "Mechanical HoD", "Material & Movement"];
  var dummyDates = ["15-Apr-26", "30-Apr-26", "15-May-26", "31-May-26"];
  var ownerIdx = 0;

  WTG_DOCS.forEach(function(d) {
    var s = docs[d.id];
    if (!s.done && !s.na) {
      // Check if Excel already has a punch point mentioning this doc
      var alreadyCovered = excelPPs.some(function(ep) {
        return ep.desc && d.name && ep.desc.toLowerCase().indexOf(d.name.substring(0, 20).toLowerCase()) >= 0;
      });
      if (!alreadyCovered) {
        autoPPs.push({
          sno: ppIdx++,
          docId: d.id,
          desc: d.name,
          itemId: d.itemId,
          itemMetric: d.itemMetric,
          blocks: blocksWhat(d.layer),
          layer: d.layer,
          scope: d.scope,
          owner: dummyOwners[ownerIdx % dummyOwners.length],
          approver: dummyApprovers[ownerIdx % dummyApprovers.length],
          expectedDate: dummyDates[ownerIdx % dummyDates.length],
          ownerRemarks: "Pending — auto-generated from checklist",
          approverRemarks: "",
          closureDate: "",
          status: "Open",
        });
        ownerIdx++;
      }
    }
  });

  var allPPs = excelPPs.concat(autoPPs);
  var openPP = allPPs.filter(function(p) { return p.status !== "Closed"; }).length;
  var closedPP = allPPs.filter(function(p) { return p.status === "Closed"; }).length;

  // DevCo readiness self-assessment
  var nnOk = layerOk(WTG_ITEMS, items, "non-neg");
  var aOk = nnOk && layerOk(WTG_ITEMS, items, "ntp-a");
  var bOk = aOk && layerOk(WTG_ITEMS, items, "ntp-b");
  var readiness = "Not Ready";
  if (bOk) readiness = "Ready — Tower Assembly";
  else if (aOk) readiness = "Ready — Foundation";
  else if (nnOk) readiness = "Partial — Non-neg met";

  return {
    id: siteId + "-" + label.replace(/[^A-Za-z0-9]/g, "") + "-" + Date.now(),
    label: label, siteId: siteId, docs: docs, items: items,
    punchPoints: allPPs, openPP: openPP, closedPP: closedPP, totalPP: allPPs.length,
    offer: parsed.offer, loc: parsed.loc, readiness: readiness,
    fromExcel: true,
  };
}

// ── BUILT-IN HELPERS ─────────────────────────────────────────────────
function buildDocs(docList, ov) {
  var d = {}; docList.forEach(function(x) { d[x.id] = { done: true, na: false }; });
  if (ov) Object.keys(ov).forEach(function(k) { if (ov[k] === "na") d[k] = { done: false, na: true }; else if (ov[k] === false) d[k] = { done: false, na: false }; });
  return d;
}
function buildPP(docList, docs, ppOv) {
  var pps = [], idx = 1;
  docList.forEach(function(d) { var s = docs[d.id]; if (!s.done && !s.na) {
    var pp = { sno: idx++, docId: d.id, desc: d.name, itemId: d.itemId, itemMetric: d.itemMetric, blocks: blocksWhat(d.layer), layer: d.layer, scope: d.scope, owner: "", approver: "", expectedDate: "", ownerRemarks: "", approverRemarks: "", closureDate: "", status: "Open" };
    if (ppOv && ppOv[d.id]) { var ov2 = ppOv[d.id]; Object.keys(ov2).forEach(function(k) { pp[k] = ov2[k]; }); }
    pps.push(pp);
  }});
  return pps;
}
function mkWTG(label, siteId, docOv, offerInfo, ppOv) {
  var docs = buildDocs(WTG_DOCS, docOv);
  var items = {}; WTG_ITEMS.forEach(function(it) { items[it.id] = calcItem(it.docs, docs); });
  var pps = buildPP(WTG_DOCS, docs, ppOv);
  var oP = pps.filter(function(p) { return p.status !== "Closed"; }).length;
  var cP = pps.filter(function(p) { return p.status === "Closed"; }).length;
  return { id: siteId + "-" + label.replace(/[^A-Za-z0-9]/g, ""), label: label, siteId: siteId, docs: docs, items: items, punchPoints: pps, openPP: oP, closedPP: cP, totalPP: pps.length, offer: offerInfo || { date: "", siteHead: "Pending", legalIC: "Pending", techIC: "Pending", acceptance: "Pending", projMgr: "Pending", mechHoD: "Pending", matMove: "Pending" }, readiness: "", fromExcel: false };
}

function effectiveStatus(wtg, sl) {
  var sNN = layerOk(SITE_ITEMS, sl.items, "non-neg");
  var wNN = layerOk(WTG_ITEMS, wtg.items, "non-neg") && sNN;
  var wA = wNN && layerOk(WTG_ITEMS, wtg.items, "ntp-a");
  var wB = wA && layerOk(WTG_ITEMS, wtg.items, "ntp-b") && sl.ntpBOk;
  var all = WTG_ITEMS.every(function(it) { return wtg.items[it.id].status === "complete" || wtg.items[it.id].status === "na"; }) && sl.allClear;
  if (all) return "full"; if (wB) return "cond-tower"; if (wA) return "cond-fdn"; return "no-hoto";
}

function getDocStats(wtg) {
  var t = 0, d = 0, n = 0;
  WTG_DOCS.forEach(function(x) { var s = wtg.docs[x.id]; if (s && s.na) n++; else { t++; if (s && s.done) d++; } });
  return { done: d, total: t, na: n, pct: t > 0 ? Math.round((d / t) * 100) : 100 };
}

// ── SITE DATA ────────────────────────────────────────────────────────
var SITES = [
  { id: "bellary", name: "KA Bellary Wind-1", model: "S144 3.15MW", circle: "C3", customer: "NTPC Ltd", cod: "Dec-27" },
  { id: "zephyr", name: "TN Tirunelveli Zephyr", model: "S120 2.1MW", circle: "C4", customer: "Infosys Green Energy", cod: "Mar-27" },
  { id: "upload", name: "Uploaded Site", model: "—", circle: "—", customer: "—", cod: "—" },
];

var SITE_LEVEL = {
  bellary: { docOv: {}, ppOv: {} },
  zephyr: { docOv: { PS03b: false }, ppOv: { PS03b: { owner: "Site Legal I/C", approver: "Material & Movement", expectedDate: "20-Mar-26", status: "In Progress", ownerRemarks: "Yard lease negotiation — 80% agreed" } } },
  upload: { docOv: {}, ppOv: {} },
};

function buildSL(siteId) {
  var sd = SITE_LEVEL[siteId] || { docOv: {}, ppOv: {} };
  var docs = buildDocs(SITE_DOCS, sd.docOv);
  var items = {}; SITE_ITEMS.forEach(function(it) { items[it.id] = calcItem(it.docs, docs); });
  var pps = buildPP(SITE_DOCS, docs, sd.ppOv);
  return { docs: docs, items: items, pps: pps, ntpBOk: layerOk(SITE_ITEMS, items, "ntp-b"), allClear: SITE_ITEMS.every(function(it) { return items[it.id].status === "complete" || items[it.id].status === "na"; }), openPP: pps.filter(function(p) { return p.status !== "Closed"; }).length, closedPP: pps.filter(function(p) { return p.status === "Closed"; }).length, totalPP: pps.length };
}

// Built-in demo WTGs
function demoWTGs(siteId) {
  var F = function(d) { return { date: d, siteHead: "Approved", legalIC: "Approved", techIC: "Approved", acceptance: "Full HOTO", projMgr: "Approved", mechHoD: "Approved", matMove: "Approved" }; };
  var CF = function(d) { return { date: d, siteHead: "Approved", legalIC: "Approved", techIC: "Approved", acceptance: "Conditional (Foundation)", projMgr: "Cond. Approved", mechHoD: "Pending", matMove: "Pending" }; };
  var CT = function(d) { return { date: d, siteHead: "Approved", legalIC: "Approved", techIC: "Approved", acceptance: "Conditional (Tower Assembly)", projMgr: "Cond. Approved", mechHoD: "Cond. Approved", matMove: "Approved" }; };
  var N = { date: "", siteHead: "Pending", legalIC: "Pending", techIC: "Pending", acceptance: "Pending", projMgr: "Pending", mechHoD: "Pending", matMove: "Pending" };

  if (siteId === "bellary") return [
    mkWTG("WTG-001", "bellary", { PM07a: "na", PM08a: "na" }, F("15-Jan-26")),
    mkWTG("WTG-002", "bellary", { LT01c: "na", LT06a: "na", LT06b: "na", LT06c: "na", FC01b: "na", PM07a: "na", PM08a: "na" }, F("20-Jan-26")),
    mkWTG("WTG-003", "bellary", { RA05b: false, PS05a: false, PS05b: false, CC01a: false, CC01b: false, PS07a: false, PS07b: false, RA07a: false, RA07b: false, CC02a: false, CC02b: false, CL01a: false, PM07a: "na", PM08a: "na" }, CF("01-Feb-26"), { RA05b: { owner: "Site Legal I/C", approver: "Project Interface Mgr", expectedDate: "31-Mar-26", status: "In Progress", ownerRemarks: "Easement deed drafted, farmer review pending" } }),
    mkWTG("WTG-004", "bellary", { LT01c: false, FC01b: false, PS05a: false, PS05b: false, CC01b: false, PS07a: false, PS07b: false, RA07a: false, RA07b: false, CC02a: false, CC02b: false, CL01a: false, PM07a: "na", PM08a: "na" }, CF("10-Feb-26"), { LT01c: { owner: "Site Legal I/C", approver: "Project Interface Mgr", expectedDate: "28-Feb-26", status: "Resolved", ownerRemarks: "NOC obtained from SBI", approverRemarks: "Under review" }, FC01b: { owner: "Site Head", approver: "Project Interface Mgr", expectedDate: "15-Mar-26", status: "In Progress", ownerRemarks: "Escrow setup in progress" } }),
    mkWTG("WTG-005", "bellary", { PS07a: false, PS07b: false, RA07a: false, RA07b: false, CC02a: false, CC02b: false, CL01a: false, PM07a: "na", PM08a: "na" }, CT("05-Jan-26"), { RA07a: { owner: "Site Tech I/C", approver: "Mechanical HoD", expectedDate: "31-Mar-26", status: "In Progress", ownerRemarks: "Road widening 60%, CBR testing scheduled" } }),
    mkWTG("WTG-006", "bellary", { LT03a: false, LT05a: false, PS01a: false, RA01a: false, PM03a: false, PM04a: false, PM06a: false, PM06b: false, PM07a: "na", PM08a: "na", RA02a: false, RA03a: false, RA05a: false, RA05b: false, RA06a: false, LT04a: false, LT06a: "na", LT06b: "na", LT06c: "na", PS06a: false, PS06b: false, PS05a: false, PS05b: false, CC01a: false, CC01b: false, PS07a: false, PS07b: false, RA07a: false, RA07b: false, CC02a: false, CC02b: false, CL01a: false }, N),
  ];
  if (siteId === "zephyr") return [
    mkWTG("WTG-001", "zephyr", { PM07a: "na", PM08a: "na" }, CT("10-Feb-26")),
    mkWTG("WTG-002", "zephyr", { PS02c: false, FC01c: false, RA05b: false, PS06a: false, PS05a: false, PS05b: false, CC01a: false, CC01b: false, PS07a: false, PS07b: false, RA07a: false, RA07b: false, CC02a: false, CC02b: false, CL01a: false, PM07a: "na", PM08a: "na" }, CF("20-Feb-26"), { PS02c: { owner: "Site Head", approver: "Project Interface Mgr", expectedDate: "10-Mar-26", status: "In Progress", ownerRemarks: "Compiling obstruction photos" } }),
    mkWTG("WTG-003", "zephyr", { FC01b: "na", PM07a: "na", PM08a: "na" }, CT("18-Feb-26")),
  ];
  return [];
}

// ── PALETTE & COMPONENTS ─────────────────────────────────────────────
var P = { bg: "#f3f4f6", surface: "#fff", surfaceAlt: "#f9fafb", highlight: "#f0faf8", border: "#ddd", borderLight: "#e5e7eb", text: "#1f2937", textMuted: "#6b7280", textDim: "#9ca3af", primary: "#006551", secondary: "#009299", green: "#16a34a", greenBg: "#f0fdf4", amber: "#d97706", amberBg: "#fffbeb", red: "#dc2626", redBg: "#fef2f2", purple: "#7c3aed", blue: "#2563eb", blueBg: "#eff6ff" };
var HM = { "full": { label: "Full HOTO", bg: P.green }, "cond-tower": { label: "Cond. — Tower", bg: P.blue }, "cond-fdn": { label: "Cond. — Foundation", bg: P.amber }, "no-hoto": { label: "No HOTO", bg: P.red } };

function B({ color, bg, children }) { return <span style={{ display: "inline-flex", alignItems: "center", fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 3, background: bg || "#eee", color: color || P.text, letterSpacing: 0.3, textTransform: "uppercase", whiteSpace: "nowrap" }}>{children}</span>; }
function HB({ status }) { var m = HM[status]; return <B color="#fff" bg={m.bg}>{m.label}</B>; }
function PB({ status }) { var m = { Open: P.redBg, "In Progress": P.amberBg, Resolved: P.blueBg, Closed: P.greenBg }; var c = { Open: P.red, "In Progress": P.amber, Resolved: P.blue, Closed: P.green }; return <B color={c[status] || P.red} bg={m[status] || P.redBg}>{status}</B>; }
function RB({ readiness }) { if (!readiness) return null; var c = readiness.indexOf("Ready") === 0 ? P.green : readiness.indexOf("Partial") === 0 ? P.amber : P.red; return <B color="#fff" bg={c}>{readiness}</B>; }
function Bar({ pct, h }) { var c = pct >= 85 ? P.green : pct >= 70 ? P.amber : P.red; return <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ flex: 1, height: h || 5, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}><div style={{ width: pct + "%", height: "100%", background: c, borderRadius: 4 }} /></div><span style={{ fontSize: 10, fontWeight: 700, color: c, minWidth: 32, textAlign: "right" }}>{pct}%</span></div>; }
function Met({ label, value, color, sub }) { return <div style={{ background: P.surface, borderRadius: 6, padding: "10px 14px", border: "1px solid " + P.border }}><div style={{ fontSize: 9, color: P.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3, fontWeight: 500 }}>{label}</div><div style={{ fontSize: 20, fontWeight: 800, color: color || P.text, lineHeight: 1.1 }}>{value}</div>{sub && <div style={{ fontSize: 9, color: P.textDim, marginTop: 2 }}>{sub}</div>}</div>; }
function SH({ children }) { return <div style={{ fontSize: 11, fontWeight: 700, color: P.secondary, padding: "8px 0 4px", borderBottom: "2px solid " + P.secondary, marginBottom: 6, marginTop: 12 }}>{children}</div>; }

function PPTbl({ pps, title }) {
  if (!pps || !pps.length) return null;
  var op = pps.filter(function(p) { return p.status !== "Closed"; }).length;
  return <div><SH>{title || "Punch Points"} ({op} open of {pps.length})</SH>
    <div style={{ overflowX: "auto", border: "1px solid " + P.border, borderRadius: 4 }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
      <thead><tr style={{ background: P.primary, color: "#fff" }}>{["#", "Description", "Blocks", "Owner", "Approver", "Target", "Owner Remarks", "Approver Remarks", "Status", "Closed"].map(function(h) { return <th key={h} style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 500, whiteSpace: "nowrap" }}>{h}</th>; })}</tr></thead>
      <tbody>{pps.map(function(pp, i) { return <tr key={i} style={{ background: i % 2 ? P.surfaceAlt : P.surface, borderBottom: "1px solid " + P.borderLight }}>
        <td style={{ padding: "5px 6px", textAlign: "center", color: P.textDim, fontWeight: 700 }}>{pp.sno}</td>
        <td style={{ padding: "5px 6px", maxWidth: 200 }}><div style={{ fontWeight: 500, lineHeight: 1.3 }}>{pp.desc}</div>{pp.itemId && <div style={{ fontSize: 8, color: P.textDim }}>{pp.itemId}</div>}</td>
        <td style={{ padding: "5px 6px", fontSize: 9, fontWeight: 600, color: pp.blocks === "Foundation" ? P.amber : pp.blocks === "Tower Assembly" ? P.blue : pp.blocks === "Erection" ? P.purple : P.textMuted }}>{pp.blocks || "—"}</td>
        <td style={{ padding: "5px 6px", whiteSpace: "nowrap" }}>{pp.owner || "—"}</td>
        <td style={{ padding: "5px 6px", whiteSpace: "nowrap" }}>{pp.approver || "—"}</td>
        <td style={{ padding: "5px 6px", whiteSpace: "nowrap", fontWeight: 600 }}>{pp.expectedDate || "—"}</td>
        <td style={{ padding: "5px 6px", color: P.textMuted, maxWidth: 130, fontSize: 9 }}>{pp.ownerRemarks || "—"}</td>
        <td style={{ padding: "5px 6px", color: P.textMuted, maxWidth: 130, fontSize: 9 }}>{pp.approverRemarks || "—"}</td>
        <td style={{ padding: "5px 6px" }}><PB status={pp.status} /></td>
        <td style={{ padding: "5px 6px", fontWeight: 600, color: pp.closureDate ? P.green : P.textDim }}>{pp.closureDate || "—"}</td>
      </tr>; })}</tbody></table></div></div>;
}

var LAYER_TAG = {
  "non-neg": { label: "HOTO Mandatory", color: P.red },
  "ntp-a": { label: "Foundation Ready", color: P.amber },
  "ntp-b": { label: "LTA Ready", color: P.blue },
  "ntp-c": { label: "Erection Ready", color: P.purple },
  "general": { label: "Full HOTO", color: P.textMuted },
};

function ItemRow({ it, st, docs, idx }) {
  var sc = st.status === "complete" ? P.green : st.status === "partial" ? P.amber : st.status === "na" ? P.textDim : P.red;
  var lt = LAYER_TAG[it.layer] || LAYER_TAG["general"];
  return <div style={{ padding: "4px 8px", borderBottom: "1px solid " + P.borderLight, borderLeftWidth: 3, borderLeftStyle: "solid", borderLeftColor: sc, background: idx % 2 ? P.surfaceAlt : P.surface, display: "flex", alignItems: "flex-start", gap: 8 }}>
    <div style={{ minWidth: 90, flexShrink: 0, paddingTop: 2 }}>
      <span style={{ fontSize: 9, fontWeight: 600, color: lt.color }}>{lt.label}</span>
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
        <div style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontWeight: 700, color: P.textDim, fontSize: 9 }}>{it.id}</span>
          {it.scope === "site" && <B color={P.primary} bg={P.highlight}>SITE</B>}
          <span style={{ fontWeight: 500 }}>{it.metric}</span>
          <span style={{ color: P.textDim, fontSize: 9 }}>{st.done}/{st.total - st.na}{st.na > 0 ? " (+" + st.na + " N/A)" : ""}</span>
        </div>
        <B color="#fff" bg={sc}>{st.status === "complete" ? "Done" : st.status === "partial" ? "Partial" : st.status === "na" ? "N/A" : "Pending"}</B>
      </div>
      {(st.status === "partial" || st.status === "incomplete") && <div style={{ marginTop: 2, marginLeft: 12 }}>{it.docs.map(function(d) {
        var dd = docs[d.id]; if (!dd) return null;
        if (dd.na) return <div key={d.id} style={{ fontSize: 9, color: P.textDim }}>— {d.name} (N/A)</div>;
        return <div key={d.id} style={{ fontSize: 9, color: dd.done ? P.green : P.red }}>{dd.done ? "✓" : "✗"} {d.name}</div>;
      })}</div>}
    </div>
  </div>;
}

// ── PDF EXPORT ───────────────────────────────────────────────────────
function exportPDF(wtg, site, sl) {
  var ds = getDocStats(wtg);
  var loc = wtg.loc || {};
  var hm = HM[wtg.hotoStatus];
  var catSummary = CATS.map(function(cat) {
    var comp = 0, inc = 0, na = 0;
    cat.items.forEach(function(it) {
      var st = it.scope === "site" ? sl.items[it.id] : wtg.items[it.id];
      if (!st) return;
      if (st.status === "complete") comp += (st.total - st.na);
      else if (st.status === "na") na += st.total;
      else { comp += st.done; inc += (st.total - st.na - st.done); na += st.na; }
    });
    var tot = comp + inc + na;
    var pct = (comp + inc) > 0 ? Math.round((comp / (comp + inc)) * 100) : 0;
    return { name: cat.name, comp: comp, inc: inc, na: na, tot: tot, pct: pct };
  });
  var totalComp = catSummary.reduce(function(a, c) { return a + c.comp; }, 0);
  var totalInc = catSummary.reduce(function(a, c) { return a + c.inc; }, 0);
  var totalNA = catSummary.reduce(function(a, c) { return a + c.na; }, 0);
  var totalPct = (totalComp + totalInc) > 0 ? Math.round((totalComp / (totalComp + totalInc)) * 100) : 0;

  var ppRows = (wtg.punchPoints || []).map(function(pp) {
    return "<tr><td>" + pp.sno + "</td><td>" + esc(pp.desc) + (pp.itemId ? "<br/><small>" + pp.itemId + "</small>" : "") + "</td><td>" + esc(pp.blocks || "") + "</td><td>" + esc(pp.owner) + "</td><td>" + esc(pp.approver) + "</td><td>" + esc(pp.expectedDate) + "</td><td>" + esc(pp.ownerRemarks) + "</td><td>" + esc(pp.approverRemarks) + "</td><td>" + esc(pp.status) + "</td><td>" + esc(pp.closureDate) + "</td></tr>";
  }).join("");

  var catRows = catSummary.map(function(c) {
    return "<tr><td>" + c.name + "</td><td>" + c.comp + "</td><td>" + c.inc + "</td><td>" + c.na + "</td><td>" + c.tot + "</td><td>" + c.pct + "%</td></tr>";
  }).join("");

  var html = "<!DOCTYPE html><html><head><title>HOTO - " + esc(wtg.label) + "</title><style>" +
    "body{font-family:Calibri,Arial,sans-serif;font-size:11px;margin:20px;color:#1f2937}" +
    "h1{font-size:16px;text-align:center;margin:0 0 4px;color:#006551}" +
    "h2{font-size:13px;color:#006551;margin:16px 0 6px;border-bottom:2px solid #006551;padding-bottom:2px}" +
    ".sub{text-align:center;font-size:9px;color:#666;margin-bottom:12px}" +
    "table{width:100%;border-collapse:collapse;margin-bottom:10px}" +
    "th,td{border:1px solid #999;padding:4px 6px;text-align:left;font-size:10px}" +
    "th{background:#006551;color:#fff;font-weight:600;font-size:9px}" +
    ".hdr td:first-child{font-weight:600;background:#f0faf8;width:180px}" +
    ".status-box{display:inline-block;padding:2px 8px;border-radius:3px;font-weight:700;font-size:10px;color:#fff}" +
    ".sign-grid td{height:60px;vertical-align:top;width:14.28%}" +
    ".sign-grid .role{font-weight:600;font-size:9px;text-align:center;background:#f0faf8}" +
    "@media print{body{margin:10px}h2{page-break-after:avoid}.no-break{page-break-inside:avoid}}" +
    "@page{size:A4 landscape;margin:15mm}" +
    "</style></head><body>" +

    // Page 1: HOTO Acceptance Form
    "<h1>HOTO ACCEPTANCE FORM</h1>" +
    "<div class='sub'>Wind DevCo Governance · " + esc(site.name) + "</div>" +

    // Location Details
    "<h2>Location Details</h2>" +
    "<table class='hdr'>" +
    "<tr><td>WTG Location</td><td>" + esc(loc.wtgLocation || wtg.label) + "</td><td>WTG Model</td><td>" + esc(loc.wtgModel || site.model) + "</td><td>WTG Capacity</td><td>" + esc(loc.wtgCapacity || "") + "</td><td>Site</td><td>" + esc(loc.site || site.name) + "</td></tr>" +
    "<tr><td>Village</td><td>" + esc(loc.village || "") + "</td><td>Taluka</td><td>" + esc(loc.taluka || "") + "</td><td>District</td><td>" + esc(loc.district || "") + "</td><td>State</td><td>" + esc(loc.state || "") + "</td></tr>" +
    "<tr><td>Latitude</td><td>" + esc(loc.lat || "") + "</td><td>Longitude</td><td>" + esc(loc.lng || "") + "</td><td>UTM</td><td>" + esc(loc.utm || "") + "</td><td>Financial Year</td><td>" + esc(loc.fy || "") + "</td></tr>" +
    "<tr><td>Customer</td><td>" + esc(loc.customer || site.customer) + "</td><td>Type of Soil</td><td>" + esc(loc.soilType || "") + "</td><td colspan='4'></td></tr>" +
    "</table>" +

    // Handover Details
    "<h2>Handover Details</h2>" +
    "<table class='hdr'>" +
    "<tr><td>Foundation Workfront Date</td><td>" + esc(loc.foundationDate || "") + "</td><td>HOTO Offer Date</td><td>" + esc(wtg.offer.date || loc.offerDate || "") + "</td></tr>" +
    "<tr><td>HOTO Status</td><td><span class='status-box' style='background:" + hm.bg + "'>" + hm.label + "</span></td><td>Final HOTO Date</td><td>" + esc(loc.finalHotoDate || "") + "</td></tr>" +
    "</table>" +

    // Checklist Summary
    "<h2>Checklist Summary</h2>" +
    "<table><thead><tr><th>Category</th><th>Complete</th><th>Incomplete</th><th>N/A</th><th>Total</th><th>Completion %</th></tr></thead><tbody>" +
    catRows +
    "<tr style='font-weight:700;background:#f0faf8'><td>Total</td><td>" + totalComp + "</td><td>" + totalInc + "</td><td>" + totalNA + "</td><td>" + (totalComp + totalInc + totalNA) + "</td><td>" + totalPct + "%</td></tr>" +
    "</tbody></table>" +

    // Land Team Offer
    "<h2>Land Team (Offer)</h2>" +
    "<table class='hdr'><tr><td>Offer Date</td><td colspan='5'>" + esc(wtg.offer.date || "") + "</td></tr>" +
    "<tr><td>Site Head</td><td>" + esc(wtg.offer.siteHead) + "</td><td>Site Legal I/C</td><td>" + esc(wtg.offer.legalIC) + "</td><td>Site Tech I/C</td><td>" + esc(wtg.offer.techIC) + "</td></tr></table>" +

    // Projects Team Acceptance
    "<h2>Projects Team (Acceptance)</h2>" +
    "<table class='hdr'><tr><td>Acceptance Status</td><td colspan='5'>" + esc(wtg.offer.acceptance || "") + "</td></tr>" +
    "<tr><td>Project Interface Mgr</td><td>" + esc(wtg.offer.projMgr) + "</td><td>Mechanical HoD</td><td>" + esc(wtg.offer.mechHoD) + "</td><td>Material & Movement</td><td>" + esc(wtg.offer.matMove) + "</td></tr></table>" +

    // Sign-off Section
    "<h2>HOTO Handover / Takeover Team</h2>" +
    "<table class='sign-grid'>" +
    "<tr class='role'><td>Function</td><td>Land Surveyor</td><td>Land Technical</td><td>Legal In Charge</td><td>Land Site In Charge</td><td>Circle Head</td><td>Project / Construction Mgr</td></tr>" +
    "<tr><td style='font-weight:600'>Name</td><td></td><td></td><td></td><td></td><td></td><td></td></tr>" +
    "<tr><td style='font-weight:600'>Comments</td><td></td><td></td><td></td><td></td><td></td><td></td></tr>" +
    "<tr><td style='font-weight:600'>Sign & Date</td><td></td><td></td><td></td><td></td><td></td><td></td></tr>" +
    "</table>";

  // Annexure: Punch Points
  if (wtg.punchPoints && wtg.punchPoints.length > 0) {
    html += "<div style='page-break-before:always'></div>" +
      "<h2>Annexure — Punch Point Tracker (" + wtg.punchPoints.length + " items)</h2>" +
      "<table><thead><tr><th>#</th><th>Punch Point Description</th><th>Blocks</th><th>Owner (DevCo)</th><th>Approver (Projects)</th><th>Target Date</th><th>Owner Remarks</th><th>Approver Remarks</th><th>Status</th><th>Closure Date</th></tr></thead><tbody>" +
      ppRows +
      "</tbody></table>";
  }

  html += "</body></html>";

  var w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(function() { w.print(); }, 500); }
}
function esc(s) { return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); }

// ── MAIN APP ─────────────────────────────────────────────────────────
export default function HOTOTracker() {
  var _s = useState(0), siteIdx = _s[0], setSiteIdx = _s[1];
  var _v = useState("summary"), view = _v[0], setView = _v[1];
  var _w = useState(null), selWTG = _w[0], setSelWTG = _w[1];
  var _c = useState(false), showCL = _c[0], setCL = _c[1];
  var _f = useState("all"), filter = _f[0], setFilter = _f[1];
  var _u = useState([]), uploaded = _u[0], setUploaded = _u[1];
  var _un = useState(""), uploadSiteName = _un[0], setUploadSiteName = _un[1];

  var site = SITES[siteIdx];
  var displaySite = useMemo(function() {
    if (site.id !== "upload" || !uploadSiteName) return site;
    // Derive site details from first uploaded WTG
    var firstUp = uploaded.find(function(u) { return u.siteId === "upload" && u.loc; });
    if (firstUp && firstUp.loc) {
      return { ...site, name: firstUp.loc.site || uploadSiteName, model: firstUp.loc.wtgModel || "—", customer: firstUp.loc.customer || "—", circle: firstUp.loc.state || "—", cod: firstUp.loc.fy || "—" };
    }
    return { ...site, name: uploadSiteName };
  }, [site, uploadSiteName, uploaded]);
  var sl = useMemo(function() { return buildSL(site.id); }, [site.id]);
  var builtIn = useMemo(function() { return demoWTGs(site.id); }, [site.id]);
  var allWTGs = useMemo(function() {
    var base = site.id === "upload" ? [] : builtIn;
    var upForSite = uploaded.filter(function(u) { return u.siteId === site.id; });
    return base.concat(upForSite);
  }, [site.id, builtIn, uploaded]);

  var wtgs = useMemo(function() { return allWTGs.map(function(w) { return { ...w, hotoStatus: effectiveStatus(w, sl) }; }); }, [allWTGs, sl]);
  var stats = useMemo(function() {
    var c = { full: 0, "cond-tower": 0, "cond-fdn": 0, "no-hoto": 0 }; var po = 0, pc = 0, pt = 0;
    wtgs.forEach(function(w) { c[w.hotoStatus]++; pt += w.totalPP; po += w.openPP; pc += w.closedPP; });
    return { counts: c, ppOpen: po + sl.openPP, ppClosed: pc + sl.closedPP, ppTotal: pt + sl.totalPP, total: wtgs.length };
  }, [wtgs, sl]);

  var filtered = useMemo(function() { if (filter === "all") return wtgs; return wtgs.filter(function(w) { return w.hotoStatus === filter; }); }, [wtgs, filter]);
  var cur = selWTG ? wtgs.find(function(w) { return w.id === selWTG; }) : null;

  var handleUpload = useCallback(function(e) {
    var files = e.target.files;
    if (!files || !files.length) return;
    Array.from(files).forEach(function(file) {
      var reader = new FileReader();
      reader.onload = function(ev) {
        var parsed = parseExcel(ev.target.result);
        if (parsed.parsed) {
          var label = parsed.loc.wtgLocation || file.name.replace(/\.xlsx?$/i, "");
          var wtg = buildFromExcel(parsed, label, site.id);
          setUploaded(function(prev) { return prev.concat([wtg]); });
          // Set site name from first upload if on upload site
          if (site.id === "upload" && !uploadSiteName && parsed.loc.site) {
            setUploadSiteName(parsed.loc.site);
          }
        }
      };
      reader.readAsArrayBuffer(file);
    });
    e.target.value = "";
  }, [site.id]);

  var sel = { background: P.surface, border: "1px solid " + P.border, color: P.text, fontSize: 10, padding: "4px 8px", borderRadius: 4, outline: "none" };

  function siteBlocks(w) {
    if (!w) return [];
    var msgs = [];
    SITE_ITEMS.forEach(function(it) { var s = sl.items[it.id]; if (s.status !== "complete" && s.status !== "na") msgs.push({ item: it.id + " · " + it.metric, blocks: blocksWhat(it.layer), done: s.done, total: s.total - s.na }); });
    return msgs;
  }

  return <div style={{ minHeight: "100vh", background: P.bg, color: P.text, fontFamily: "Calibri,system-ui,sans-serif", fontSize: 12, padding: 8 }}>
    <div style={{ background: P.primary, color: "#fff", borderRadius: 6, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
      <div><div style={{ fontSize: 14, fontWeight: 700 }}>HOTO Tracker</div><div style={{ fontSize: 9, opacity: 0.8 }}>Land → Projects Handover · Wind DevCo Governance</div></div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select value={siteIdx} onChange={function(e) { setSiteIdx(Number(e.target.value)); setView("summary"); setSelWTG(null); setCL(false); }} style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", fontSize: 11, padding: "5px 10px", borderRadius: 4, outline: "none" }}>
          {SITES.map(function(s, i) { return <option key={s.id} value={i} style={{ color: "#333" }}>{s.id === "upload" && uploadSiteName ? uploadSiteName : s.name}</option>; })}
        </select>
        <label style={{ background: "#fff", color: P.primary, fontSize: 10, fontWeight: 700, padding: "5px 12px", borderRadius: 4, cursor: "pointer" }}>
          Upload Excel
          <input type="file" accept=".xlsx,.xls" multiple onChange={handleUpload} style={{ display: "none" }} />
        </label>
      </div>
    </div>

    {view !== "summary" && <div style={{ padding: "5px 8px", background: P.surface, border: "1px solid " + P.border, borderRadius: 4, marginBottom: 6, display: "flex", alignItems: "center", gap: 5, fontSize: 10 }}>
      <span onClick={function() { setView("summary"); setSelWTG(null); setCL(false); }} style={{ color: P.primary, cursor: "pointer", fontWeight: 600 }}>Summary</span>
      {cur && <><span style={{ color: P.textDim }}>›</span><span style={{ color: P.textMuted }}>{cur.label}</span></>}
    </div>}

    <div style={{ background: P.surface, borderRadius: 6, boxShadow: "0 1px 2px rgba(0,0,0,.1)", padding: 12 }}>

      {view === "summary" && <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: P.primary }}>{displaySite.name}</div>
        <div style={{ fontSize: 10, color: P.textMuted, marginBottom: 12 }}>{displaySite.circle} · {displaySite.model} · {wtgs.length} WTG locations · CoD {displaySite.cod} · {displaySite.customer}</div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8, marginBottom: 12 }}>
          <Met label="Full HOTO" value={stats.counts.full} color={P.green} sub="All activities cleared" />
          <Met label="Cond. — Tower" value={stats.counts["cond-tower"]} color={P.blue} sub="Erection pending" />
          <Met label="Cond. — Foundation" value={stats.counts["cond-fdn"]} color={P.amber} sub="Tower & Erection pending" />
          <Met label="No HOTO" value={stats.counts["no-hoto"]} color={P.red} sub="Non-negotiables incomplete" />
        </div>

        {stats.ppTotal > 0 && <div style={{ background: P.amberBg, borderRadius: 4, border: "1px solid " + P.amber, padding: "8px 12px", marginBottom: 12, fontSize: 10 }}>
          <b style={{ color: P.amber }}>Punch Points:</b> <b>{stats.ppTotal}</b> total — <b style={{ color: P.red }}>{stats.ppOpen}</b> open, <b style={{ color: P.green }}>{stats.ppClosed}</b> closed
        </div>}

        <SH>Site-Level Items</SH>
        <div style={{ border: "1px solid " + P.border, borderRadius: 4, overflow: "hidden", marginBottom: 4 }}>
          {SITE_ITEMS.map(function(it, idx) { return <ItemRow key={it.id} it={it} st={sl.items[it.id]} docs={sl.docs} idx={idx} />; })}</div>
        {sl.totalPP > 0 && <PPTbl pps={sl.pps} title="Site-Level Punch Points" />}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
          <SH>WTG Locations</SH>
          <select value={filter} onChange={function(e) { setFilter(e.target.value); }} style={sel}>
            <option value="all">All ({wtgs.length})</option>
            {Object.keys(HM).map(function(k) { return <option key={k} value={k}>{HM[k].label} ({stats.counts[k]})</option>; })}
          </select>
        </div>
        <div style={{ overflowX: "auto", border: "1px solid " + P.border, borderRadius: 4 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr style={{ background: P.primary, color: "#fff" }}>{["WTG", "Status", "DevCo Readiness", "Completion", "Punch Points", "Offer Date", ""].map(function(h) { return <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontSize: 10, fontWeight: 500 }}>{h}</th>; })}</tr></thead>
            <tbody>{filtered.map(function(w, i) { var ds = getDocStats(w); var isAccepted = w.offer.acceptance && w.offer.acceptance.indexOf("Full") >= 0; return <tr key={w.id} style={{ background: i % 2 ? P.surfaceAlt : P.surface, borderBottom: "1px solid " + P.borderLight, cursor: "pointer" }} onClick={function() { setSelWTG(w.id); setView("wtgDetail"); setCL(false); }} onMouseEnter={function(e) { e.currentTarget.style.background = P.highlight; }} onMouseLeave={function(e) { e.currentTarget.style.background = i % 2 ? P.surfaceAlt : P.surface; }}>
              <td style={{ padding: "6px 8px", fontWeight: 700, color: P.primary }}>{w.label}{w.fromExcel && <span style={{ fontSize: 8, color: P.blue, marginLeft: 4 }}>UPLOADED</span>}</td>
              <td style={{ padding: "6px 8px" }}><HB status={w.hotoStatus} /></td>
              <td style={{ padding: "6px 8px" }}>{!isAccepted ? <RB readiness={w.readiness || (w.hotoStatus === "no-hoto" ? "Not Ready" : w.hotoStatus === "cond-fdn" ? "Ready — Foundation" : w.hotoStatus === "cond-tower" ? "Ready — Tower Assembly" : "Ready — Full")} /> : <span style={{ fontSize: 9, color: P.green }}>Accepted</span>}</td>
              <td style={{ padding: "6px 8px", minWidth: 120 }}><Bar pct={ds.pct} h={4} /></td>
              <td style={{ padding: "6px 8px", fontSize: 10 }}>{w.totalPP > 0 ? <span>{w.openPP > 0 && <b style={{ color: P.red }}>{w.openPP} open</b>}{w.openPP > 0 && w.closedPP > 0 && " · "}{w.closedPP > 0 && <span style={{ color: P.green }}>{w.closedPP} closed</span>}</span> : <span style={{ color: P.textDim }}>—</span>}</td>
              <td style={{ padding: "6px 8px", fontSize: 10, color: P.textMuted }}>{w.offer.date || "—"}</td>
              <td style={{ padding: "6px 8px", fontSize: 10, color: P.primary }}>→</td>
            </tr>; })}</tbody>
          </table>
        </div>
      </div>}

      {view === "wtgDetail" && cur && (function() {
        var ds = getDocStats(cur); var m = HM[cur.hotoStatus]; var sb = siteBlocks(cur);
        var isAccepted = cur.offer.acceptance && cur.offer.acceptance.indexOf("Full") >= 0;
        return <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: P.primary }}>{cur.label}{cur.fromExcel && <span style={{ fontSize: 10, color: P.blue, marginLeft: 6 }}>UPLOADED</span>}</div>
              <div style={{ fontSize: 10, color: P.textMuted }}>{displaySite.name} · {cur.loc ? [cur.loc.village, cur.loc.taluka, cur.loc.state].filter(Boolean).join(", ") : displaySite.model}</div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {!isAccepted && <RB readiness={cur.readiness || (cur.hotoStatus === "no-hoto" ? "Not Ready" : cur.hotoStatus === "cond-fdn" ? "Ready — Foundation" : cur.hotoStatus === "cond-tower" ? "Ready — Tower Assembly" : "Ready — Full")} />}
              <HB status={cur.hotoStatus} />
              <button onClick={function() { exportPDF(cur, displaySite, sl); }} style={{ background: P.primary, color: "#fff", border: "none", padding: "5px 12px", borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Export PDF</button>
            </div>
          </div>

          {cur.loc && cur.loc.wtgLocation && <div style={{ background: P.highlight, border: "1px solid " + P.border, borderRadius: 4, padding: "8px 12px", marginBottom: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 6, fontSize: 10 }}>
            {[["WTG Location", cur.loc.wtgLocation], ["Model", cur.loc.wtgModel], ["Site", cur.loc.site], ["Village", cur.loc.village], ["State", cur.loc.state], ["Customer", cur.loc.customer], ["FY", cur.loc.fy], ["Foundation Date", cur.loc.foundationDate]].filter(function(p) { return p[1]; }).map(function(p) { return <div key={p[0]}><span style={{ color: P.textMuted }}>{p[0]}: </span><b>{p[1]}</b></div>; })}
          </div>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(100px,1fr))", gap: 6, marginBottom: 12 }}>
            <Met label="Completion" value={ds.pct + "%"} color={ds.pct >= 85 ? P.green : ds.pct >= 70 ? P.amber : P.red} sub={ds.done + " of " + ds.total + " docs"} />
            <Met label="WTG Punch Pts" value={cur.openPP} color={cur.openPP > 0 ? P.red : P.green} sub={cur.totalPP > 0 ? cur.closedPP + " closed" : "No punch list"} />
            <Met label="Offer Date" value={cur.offer.date || "—"} color={P.text} sub={cur.offer.acceptance} />
          </div>

          {sb.length > 0 && <div style={{ background: P.highlight, border: "1px solid " + P.border, borderRadius: 4, padding: "8px 12px", marginBottom: 12, fontSize: 10 }}>
            <b style={{ color: P.primary }}>Site-level items affecting this WTG:</b>
            {sb.map(function(s) { return <div key={s.item} style={{ marginTop: 3 }}><B color="#fff" bg={s.blocks === "Tower Assembly" ? P.blue : P.textMuted}>{s.blocks}</B> <span style={{ marginLeft: 4 }}>{s.item} ({s.done}/{s.total} docs)</span></div>; })}
          </div>}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            <div style={{ border: "1px solid " + P.border, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ padding: "5px 10px", background: P.primary, color: "#fff", fontSize: 10, fontWeight: 600 }}>Land Team (Offer)</div>
              <div style={{ padding: 8 }}>{[["Site Head", cur.offer.siteHead], ["Site Legal I/C", cur.offer.legalIC], ["Site Tech I/C", cur.offer.techIC]].map(function(p) { return <div key={p[0]} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid " + P.borderLight, fontSize: 10 }}><span style={{ color: P.textMuted }}>{p[0]}</span><B color="#fff" bg={p[1] === "Approved" ? P.green : P.amber}>{p[1]}</B></div>; })}</div>
            </div>
            <div style={{ border: "1px solid " + P.border, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ padding: "5px 10px", background: P.primary, color: "#fff", fontSize: 10, fontWeight: 600 }}>Projects Team (Acceptance)</div>
              <div style={{ padding: 8 }}>{[["Project Interface Mgr", cur.offer.projMgr], ["Mechanical HoD", cur.offer.mechHoD], ["Material & Movement", cur.offer.matMove]].map(function(p) { return <div key={p[0]} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid " + P.borderLight, fontSize: 10 }}><span style={{ color: P.textMuted }}>{p[0]}</span><B color="#fff" bg={p[1] === "Approved" ? P.green : String(p[1]).indexOf("Cond") >= 0 ? P.amber : "#999"}>{p[1]}</B></div>; })}</div>
            </div>
          </div>

          <PPTbl pps={cur.punchPoints} title="WTG Punch Points" />
          {sl.totalPP > 0 && <PPTbl pps={sl.pps} title="Site-Level Punch Points" />}

          {cur.hotoStatus === "no-hoto" && <div style={{ marginTop: 12 }}>
            <SH>Pending Non-Negotiable Items</SH>
            <div style={{ background: P.redBg, border: "1px solid " + P.red, borderRadius: 4, padding: 10, fontSize: 10, color: P.red, marginBottom: 8 }}>Non-negotiable items incomplete. No HOTO can be offered.</div>
            <div style={{ border: "1px solid " + P.border, borderRadius: 4, overflow: "hidden" }}>
              {WTG_ITEMS.filter(function(it) { return it.layer === "non-neg" && cur.items[it.id] && cur.items[it.id].status !== "complete" && cur.items[it.id].status !== "na"; }).map(function(it, idx) {
                var is2 = cur.items[it.id]; var pd = it.docs.filter(function(d) { return cur.docs[d.id] && !cur.docs[d.id].done && !cur.docs[d.id].na; });
                return <div key={it.id} style={{ padding: "6px 10px", borderBottom: "1px solid " + P.borderLight, background: idx % 2 ? P.surfaceAlt : P.surface }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{it.id} · {it.metric} <span style={{ fontSize: 9, color: P.textDim }}>({is2.done}/{is2.total - is2.na} docs)</span></div>
                  {pd.map(function(d) { return <div key={d.id} style={{ fontSize: 9, color: P.red, marginLeft: 12, marginTop: 2 }}>✗ {d.name}</div>; })}
                </div>;
              })}
            </div>
          </div>}

          <div style={{ marginTop: 12 }}>
            <div onClick={function() { setCL(!showCL); }} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 0 4px", borderBottom: "2px solid " + P.secondary }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: P.secondary }}>{showCL ? "▾" : "▸"} Full Checklist</span>
              <span style={{ fontSize: 9, color: P.textDim }}>{ds.done}/{ds.total} docs</span>
            </div>
            {showCL && <div style={{ marginTop: 6 }}>{CATS.map(function(cat) {
              var wI = cat.items.filter(function(it) { return it.scope === "wtg"; });
              var sI = cat.items.filter(function(it) { return it.scope === "site"; });
              if (!wI.length && !sI.length) return null;
              return <div key={cat.id} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: P.primary, padding: "4px 0", borderBottom: "1px solid " + P.borderLight, marginBottom: 2 }}>{cat.name}</div>
                {wI.map(function(it, idx) { return <ItemRow key={it.id} it={it} st={cur.items[it.id]} docs={cur.docs} idx={idx} />; })}
                {sI.map(function(it, idx) { return <ItemRow key={it.id} it={it} st={sl.items[it.id]} docs={sl.docs} idx={idx} />; })}
              </div>;
            })}</div>}
          </div>
        </div>;
      })()}

    </div>
    <div style={{ marginTop: 6, textAlign: "center", fontSize: 9, color: P.textDim }}>HOTO Tracker v8.0 · Wind DevCo Governance</div>
  </div>;
}